import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faServer,
  faDatabase,
  faNetworkWired,
  faChartLine,
  faFilter,
  faDownload,
  faSync,
  faClock,
  faArrowUp,
  faArrowDown,
  faLayerGroup,
  faHdd,
  faWifi,
  faDollarSign,
  faExclamationTriangle,
  faCheckCircle,
  faArrowTrendUp,
  faArrowTrendDown,
  faCubes,
  faChartArea
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub, faMicrosoft } from '@fortawesome/free-brands-svg-icons';

interface ResourcesTabProps {
  loading?: boolean;
}

interface ResourceMetrics {
  totalComputeHours: number;
  totalStorageGB: number;
  totalDataTransferGB: number;
  averageInstanceRate: number;
  networkIngressGB: number;
  networkEgressGB: number;
  forecastGrowth: number;
}

interface ComputeResource {
  id: string;
  cloudProvider: 'aws' | 'gcp' | 'azure' | 'github';
  account: string;
  region: string;
  instanceType: string;
  pricingModel: 'on-demand' | 'reserved' | 'spot' | 'committed';
  hoursUsed: number;
  avgCpuUtilization: number;
  cost: number;
  status: 'running' | 'stopped' | 'terminated';
}

interface StorageResource {
  id: string;
  cloudProvider: 'aws' | 'gcp' | 'azure' | 'github';
  account: string;
  region: string;
  storageType: string;
  sizeGB: number;
  objectCount: number;
  accessFrequency: 'frequent' | 'infrequent' | 'archive';
  cost: number;
}

interface NetworkResource {
  id: string;
  cloudProvider: 'aws' | 'gcp' | 'azure' | 'github';
  account: string;
  sourceRegion: string;
  targetRegion: string;
  ingressGB: number;
  egressGB: number;
  cost: number;
}

const getCloudIcon = (provider: string) => {
  switch (provider) {
    case 'aws': return faAws;
    case 'gcp': return faGoogle;
    case 'github': return faGithub;
    case 'azure': return faMicrosoft;
    default: return faServer;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'running': return 'text-green-600 bg-green-100';
    case 'stopped': return 'text-yellow-600 bg-yellow-100';
    case 'terminated': return 'text-red-600 bg-red-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const getPricingModelColor = (model: string) => {
  switch (model) {
    case 'reserved': return 'text-blue-600 bg-blue-100';
    case 'spot': return 'text-purple-600 bg-purple-100';
    case 'committed': return 'text-green-600 bg-green-100';
    default: return 'text-orange-600 bg-orange-100';
  }
};

export const ResourcesTab: React.FC<ResourcesTabProps> = ({ loading: propLoading = false }) => {
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'overview' | 'compute' | 'storage' | 'network' | 'forecast'>('overview');
  const [showFilters, setShowFilters] = useState(false);

  // Mock data to prevent API hanging issues
  const mockData = {
    summary: {
      totalComputeHours: 24567,
      totalStorageGB: 15843.2,
      totalDataTransferGB: 2134.7,
      averageInstanceRate: 0.45,
      networkIngressGB: 1245.6,
      networkEgressGB: 889.1,
      forecastGrowth: 12.5
    } as ResourceMetrics,
    
    computeResources: [
      {
        id: 'i-0123456789abcdef0',
        cloudProvider: 'aws',
        account: 'prod-us-east',
        region: 'us-east-1',
        instanceType: 'm5.xlarge',
        pricingModel: 'on-demand',
        hoursUsed: 720,
        avgCpuUtilization: 78.5,
        cost: 138.24,
        status: 'running'
      },
      {
        id: 'i-0fedcba9876543210',
        cloudProvider: 'aws',
        account: 'dev-staging',
        region: 'us-west-2',
        instanceType: 't3.medium',
        pricingModel: 'spot',
        hoursUsed: 480,
        avgCpuUtilization: 45.2,
        cost: 19.68,
        status: 'running'
      },
      {
        id: 'vm-a1b2c3d4e5f6',
        cloudProvider: 'gcp',
        account: 'prod-europe',
        region: 'europe-west1',
        instanceType: 'n1-standard-4',
        pricingModel: 'committed',
        hoursUsed: 720,
        avgCpuUtilization: 82.1,
        cost: 95.76,
        status: 'running'
      }
    ] as ComputeResource[],
    
    storageResources: [
      {
        id: 's3-customer-data-prod',
        cloudProvider: 'aws',
        account: 'prod-us-east',
        region: 'us-east-1',
        storageType: 'S3 Standard',
        sizeGB: 8542.3,
        objectCount: 1245678,
        accessFrequency: 'frequent',
        cost: 196.47
      },
      {
        id: 'gcs-analytics-backup',
        cloudProvider: 'gcp',
        account: 'prod-europe',
        region: 'europe-west1',
        storageType: 'Standard Storage',
        sizeGB: 3420.1,
        objectCount: 89234,
        accessFrequency: 'infrequent',
        cost: 68.40
      },
      {
        id: 's3-logs-archive',
        cloudProvider: 'aws',
        account: 'prod-us-east',
        region: 'us-east-1',
        storageType: 'S3 Glacier',
        sizeGB: 15678.9,
        objectCount: 456789,
        accessFrequency: 'archive',
        cost: 15.68
      }
    ] as StorageResource[],
    
    networkResources: [
      {
        id: 'nat-0123456789abcdef0',
        cloudProvider: 'aws',
        account: 'prod-us-east',
        sourceRegion: 'us-east-1',
        targetRegion: 'us-west-2',
        ingressGB: 456.7,
        egressGB: 234.5,
        cost: 34.56
      },
      {
        id: 'lb-a1b2c3d4e5f6',
        cloudProvider: 'gcp',
        account: 'prod-europe',
        sourceRegion: 'europe-west1',
        targetRegion: 'us-central1',
        ingressGB: 123.4,
        egressGB: 678.9,
        cost: 23.45
      }
    ] as NetworkResource[]
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    console.log('ResourcesTab mounted successfully');
  }, []);

  if (loading || propLoading) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
      {/* Header */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-base font-bold text-gray-900 mb-1">Resource Management</h1>
            <p className="text-xs text-gray-600">
              Monitor and optimize compute, storage, and network resources across all cloud accounts
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={loading ? faClock : faSync} className={`mr-1.5 text-xs ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors">
              <FontAwesomeIcon icon={faDownload} className="mr-1.5 text-xs" />
              Export Report
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-2">
          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-gray-600">Compute Hours</div>
                <div className="text-sm font-bold text-gray-900">{mockData.summary.totalComputeHours.toLocaleString()}</div>
                <div className="text-[9px] text-gray-500 mt-0.5">This month</div>
              </div>
              <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faServer} className="text-blue-600 text-xs" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-gray-600">Storage Used</div>
                <div className="text-sm font-bold text-gray-900">{mockData.summary.totalStorageGB.toFixed(1)} GB</div>
                <div className="text-[9px] text-gray-500 mt-0.5">All accounts</div>
              </div>
              <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faDatabase} className="text-green-600 text-xs" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-gray-600">Data Transfer</div>
                <div className="text-sm font-bold text-gray-900">{mockData.summary.totalDataTransferGB.toFixed(1)} GB</div>
                <div className="text-[9px] text-gray-500 mt-0.5">This month</div>
              </div>
              <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faNetworkWired} className="text-purple-600 text-xs" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-gray-600">Avg Rate</div>
                <div className="text-sm font-bold text-gray-900">${mockData.summary.averageInstanceRate}</div>
                <div className="text-[9px] text-gray-500 mt-0.5">Per hour</div>
              </div>
              <div className="w-6 h-6 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faDollarSign} className="text-yellow-600 text-xs" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-gray-600">Network In/Out</div>
                <div className="text-sm font-bold text-gray-900">
                  {mockData.summary.networkIngressGB.toFixed(0)}/{mockData.summary.networkEgressGB.toFixed(0)} GB
                </div>
                <div className="text-[9px] text-gray-500 mt-0.5">Ingress/Egress</div>
              </div>
              <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faWifi} className="text-indigo-600 text-xs" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-gray-600">Growth Forecast</div>
                <div className="text-sm font-bold text-gray-900">{mockData.summary.forecastGrowth}%</div>
                <div className="text-[9px] text-gray-500 mt-0.5 flex items-center">
                  <FontAwesomeIcon icon={faArrowTrendUp} className="text-green-500 mr-0.5 text-[8px]" />
                  Next month
                </div>
              </div>
              <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faChartLine} className="text-orange-600 text-xs" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border border-gray-300 rounded">
        <div className="flex space-x-1 p-1">
          <button
            onClick={() => setActiveView('overview')}
            className={`px-4 py-2 text-xs font-medium rounded transition-colors ${
              activeView === 'overview'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveView('compute')}
            className={`px-4 py-2 text-xs font-medium rounded transition-colors ${
              activeView === 'compute'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faServer} className="mr-2" />
            Compute
          </button>
          <button
            onClick={() => setActiveView('storage')}
            className={`px-4 py-2 text-xs font-medium rounded transition-colors ${
              activeView === 'storage'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faDatabase} className="mr-2" />
            Storage
          </button>
          <button
            onClick={() => setActiveView('network')}
            className={`px-4 py-2 text-xs font-medium rounded transition-colors ${
              activeView === 'network'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faNetworkWired} className="mr-2" />
            Network
          </button>
          <button
            onClick={() => setActiveView('forecast')}
            className={`px-4 py-2 text-xs font-medium rounded transition-colors ${
              activeView === 'forecast'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faChartLine} className="mr-2" />
            Forecast
          </button>
        </div>
      </div>

      {/* Content based on active view */}
      {activeView === 'overview' && (
        <div className="space-y-3">
          {/* Usage Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* Usage by Cloud Provider */}
            <div className="bg-white border border-gray-300 rounded p-2">
              <h3 className="text-[10px] font-semibold text-gray-900 mb-1.5">Usage by Cloud Provider</h3>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <FontAwesomeIcon icon={faAws} className="text-orange-600 text-xs" />
                    <span className="text-[10px] text-gray-700">AWS</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">14,567h</div>
                    <div className="text-[9px] text-gray-500">59.2%</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <FontAwesomeIcon icon={faGoogle} className="text-blue-600 text-xs" />
                    <span className="text-[10px] text-gray-700">GCP</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">7,234h</div>
                    <div className="text-[9px] text-gray-500">29.4%</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <FontAwesomeIcon icon={faMicrosoft} className="text-blue-500 text-xs" />
                    <span className="text-[10px] text-gray-700">Azure</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">2,766h</div>
                    <div className="text-[9px] text-gray-500">11.4%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage by Resource Type */}
            <div className="bg-white border border-gray-300 rounded p-2">
              <h3 className="text-[10px] font-semibold text-gray-900 mb-1.5">Usage by Type</h3>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faServer} className="text-blue-600" />
                    <span className="text-[10px] text-gray-700">Compute</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">24,567h</div>
                    <div className="text-xs text-gray-500">68.1%</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faDatabase} className="text-green-600" />
                    <span className="text-[10px] text-gray-700">Storage</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">15.8TB</div>
                    <div className="text-xs text-gray-500">22.7%</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faNetworkWired} className="text-purple-600" />
                    <span className="text-[10px] text-gray-700">Network</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">2.1TB</div>
                    <div className="text-xs text-gray-500">9.2%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage by Region */}
            <div className="bg-white border border-gray-300 rounded p-2">
              <h3 className="text-xs font-semibold text-gray-900 mb-1.5">Top Regions</h3>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">us-east-1</span>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">8,945h</div>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full mt-0.5">
                      <div className="h-full bg-blue-500 rounded-full" style={{width: '78%'}}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">us-west-2</span>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">6,123h</div>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full mt-0.5">
                      <div className="h-full bg-blue-500 rounded-full" style={{width: '53%'}}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">europe-west1</span>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">4,567h</div>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full mt-0.5">
                      <div className="h-full bg-blue-500 rounded-full" style={{width: '40%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage by Account */}
            <div className="bg-white border border-gray-300 rounded p-2">
              <h3 className="text-xs font-semibold text-gray-900 mb-1.5">Top Accounts</h3>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">prod-us-east</span>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">12,456h</div>
                    <div className="text-[10px] text-gray-500">50.7%</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">prod-europe</span>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">7,234h</div>
                    <div className="text-[10px] text-gray-500">29.4%</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">dev-staging</span>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-900">4,877h</div>
                    <div className="text-[10px] text-gray-500">19.9%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Trends and Forecasting */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {/* Usage Trends Chart */}
            <div className="bg-white border border-gray-300 rounded p-3">
              <h3 className="text-xs font-semibold text-gray-900 mb-2">Usage Trends (Last 6 Months)</h3>
              <div className="h-40 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                <div className="text-center px-2">
                  <FontAwesomeIcon icon={faChartLine} className="text-2xl text-gray-400 mb-2" />
                  <h4 className="text-xs font-medium text-gray-600 mb-1">Resource Usage Trends</h4>
                  <p className="text-[10px] text-gray-500 max-w-sm">
                    Time-series chart showing compute hours, storage usage, and network transfer trends
                  </p>
                  <div className="mt-2 flex justify-center space-x-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded mr-1"></div>
                      <span className="text-[9px] text-gray-600">Compute</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded mr-1"></div>
                      <span className="text-[9px] text-gray-600">Storage</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded mr-1"></div>
                      <span className="text-[9px] text-gray-600">Network</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Forecast */}
            <div className="bg-white border border-gray-300 rounded p-3">
              <h3 className="text-xs font-semibold text-gray-900 mb-2">Usage Forecast (Next 3 Months)</h3>
              <div className="space-y-2">
                <div className="border rounded-lg p-2 bg-blue-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-1.5">
                      <FontAwesomeIcon icon={faServer} className="text-blue-600 text-xs" />
                      <span className="text-xs font-medium text-gray-900">Compute Usage</span>
                    </div>
                    <FontAwesomeIcon icon={faArrowTrendUp} className="text-green-600 text-xs" />
                  </div>
                  <div className="text-lg font-bold text-gray-900">+{mockData.summary.forecastGrowth}%</div>
                  <div className="text-[10px] text-gray-600">Expected: 27,854h/month</div>
                </div>

                <div className="border rounded-lg p-2 bg-green-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-1.5">
                      <FontAwesomeIcon icon={faDatabase} className="text-green-600 text-xs" />
                      <span className="text-xs font-medium text-gray-900">Storage Usage</span>
                    </div>
                    <FontAwesomeIcon icon={faArrowTrendUp} className="text-green-600 text-xs" />
                  </div>
                  <div className="text-lg font-bold text-gray-900">+8.2%</div>
                  <div className="text-[10px] text-gray-600">Expected: 17.1TB/month</div>
                </div>

                <div className="border rounded-lg p-2 bg-purple-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-1.5">
                      <FontAwesomeIcon icon={faNetworkWired} className="text-purple-600 text-xs" />
                      <span className="text-xs font-medium text-gray-900">Network Transfer</span>
                    </div>
                    <FontAwesomeIcon icon={faArrowTrendUp} className="text-orange-600 text-xs" />
                  </div>
                  <div className="text-lg font-bold text-gray-900">+15.7%</div>
                  <div className="text-[10px] text-gray-600">Expected: 2.4TB/month</div>
                </div>
              </div>
            </div>
          </div>

          {/* Resource Tags Analysis */}
          <div className="bg-white border border-gray-300 rounded p-3">
            <h3 className="text-xs font-bold text-gray-900 mb-2">Resource Usage by Tags</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-1.5">Environment</h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Production</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-red-500 rounded-full" style={{width: '72%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">17.8K h</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Staging</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-yellow-500 rounded-full" style={{width: '18%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">4.4K h</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Development</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-green-500 rounded-full" style={{width: '10%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">2.4K h</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-1.5">Application</h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">web-app</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-blue-500 rounded-full" style={{width: '45%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">11.1K h</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">api-service</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-purple-500 rounded-full" style={{width: '32%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">7.9K h</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">analytics</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-orange-500 rounded-full" style={{width: '23%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">5.6K h</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-1.5">Team</h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">backend</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-indigo-500 rounded-full" style={{width: '38%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">9.4K h</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">frontend</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-pink-500 rounded-full" style={{width: '29%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">7.1K h</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">data</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-teal-500 rounded-full" style={{width: '33%'}}></div>
                      </div>
                      <span className="text-[10px] text-gray-500">8.1K h</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeView === 'compute' && (
        <div className="bg-white border border-gray-300 rounded">
          <div className="p-3 border-b border-gray-300">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Compute Resources</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                Filters
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Instance</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Account/Region</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Pricing</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Usage</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">CPU</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mockData.computeResources.map((resource) => (
                  <tr key={resource.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={getCloudIcon(resource.cloudProvider)} className="text-gray-400" />
                        <span className="text-xs font-medium text-gray-900">{resource.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-xs font-medium text-gray-900">{resource.account}</div>
                        <div className="text-[10px] text-gray-500">{resource.region}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.instanceType}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPricingModelColor(resource.pricingModel)}`}>
                        {resource.pricingModel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.hoursUsed}h</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="text-xs text-gray-900">{resource.avgCpuUtilization}%</span>
                        <div className="ml-2 w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${resource.avgCpuUtilization}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(resource.status)}`}>
                        {resource.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-900">${resource.cost}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'storage' && (
        <div className="bg-white border border-gray-300 rounded">
          <div className="p-3 border-b border-gray-300">
            <h3 className="text-sm font-bold text-gray-900">Storage Resources</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Storage</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Account/Region</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Objects</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Access</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mockData.storageResources.map((resource) => (
                  <tr key={resource.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={getCloudIcon(resource.cloudProvider)} className="text-gray-400" />
                        <span className="text-xs font-medium text-gray-900">{resource.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-xs font-medium text-gray-900">{resource.account}</div>
                        <div className="text-[10px] text-gray-500">{resource.region}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.storageType}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.sizeGB.toFixed(1)} GB</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.objectCount.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        resource.accessFrequency === 'frequent' ? 'text-green-600 bg-green-100' :
                        resource.accessFrequency === 'infrequent' ? 'text-yellow-600 bg-yellow-100' :
                        'text-blue-600 bg-blue-100'
                      }`}>
                        {resource.accessFrequency}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-900">${resource.cost}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'network' && (
        <div className="bg-white border border-gray-300 rounded">
          <div className="p-3 border-b border-gray-300">
            <h3 className="text-sm font-bold text-gray-900">Network Resources</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Resource</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Account</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Route</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Ingress</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Egress</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mockData.networkResources.map((resource) => (
                  <tr key={resource.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={getCloudIcon(resource.cloudProvider)} className="text-gray-400" />
                        <span className="text-xs font-medium text-gray-900">{resource.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-900">{resource.account}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-900">
                        {resource.sourceRegion} → {resource.targetRegion}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.ingressGB.toFixed(1)} GB</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">{resource.egressGB.toFixed(1)} GB</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-900">${resource.cost}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'forecast' && (
        <div className="space-y-3">
          {/* Usage vs Cost Growth Analysis */}
          <div className="bg-white border border-gray-300 rounded p-3">
            <h3 className="text-sm font-bold text-gray-900 mb-2">
              <FontAwesomeIcon icon={faChartLine} className="text-blue-600 mr-2" />
              Usage vs Cost Growth Analysis
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Track the relationship between resource usage growth and cost growth to measure optimization effectiveness
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
              {/* Compute Analysis */}
              <div className="border border-gray-300 rounded p-3 bg-blue-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faServer} className="text-blue-600" />
                    <span className="text-xs font-semibold text-gray-900">Compute Resources</span>
                  </div>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Usage Growth (6M)</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-blue-600 text-xs" />
                      <span className="text-xs font-medium text-blue-600">+{mockData.summary.forecastGrowth}%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Cost Growth (6M)</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-orange-600 text-xs" />
                      <span className="text-xs font-medium text-orange-600">+7.8%</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-700">Efficiency Ratio</span>
                      <span className="text-xs font-bold text-green-600">62% Better</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Cost growing slower than usage - optimizations working!
                    </div>
                  </div>
                </div>
              </div>

              {/* Storage Analysis */}
              <div className="border border-gray-300 rounded p-3 bg-green-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faDatabase} className="text-green-600" />
                    <span className="text-xs font-semibold text-gray-900">Storage Resources</span>
                  </div>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Usage Growth (6M)</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-green-600 text-xs" />
                      <span className="text-xs font-medium text-green-600">+8.2%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Cost Growth (6M)</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-orange-600 text-xs" />
                      <span className="text-xs font-medium text-orange-600">+12.4%</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-700">Efficiency Ratio</span>
                      <span className="text-xs font-bold text-yellow-600">34% Worse</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Cost growing faster than usage - needs optimization
                    </div>
                  </div>
                </div>
              </div>

              {/* Network Analysis */}
              <div className="border border-gray-300 rounded p-3 bg-purple-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faNetworkWired} className="text-purple-600" />
                    <span className="text-xs font-semibold text-gray-900">Network Resources</span>
                  </div>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Usage Growth (6M)</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-purple-600 text-xs" />
                      <span className="text-xs font-medium text-purple-600">+15.7%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Cost Growth (6M)</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-orange-600 text-xs" />
                      <span className="text-xs font-medium text-orange-600">+9.2%</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-700">Efficiency Ratio</span>
                      <span className="text-xs font-bold text-green-600">71% Better</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Excellent network optimization performance
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Historical Efficiency Trends */}
          <div className="bg-white border border-gray-300 rounded p-3">
            <h3 className="text-sm font-bold text-gray-900 mb-2">Historical Efficiency Trends</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Efficiency Chart */}
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                <div className="text-center">
                  <FontAwesomeIcon icon={faChartArea} className="text-3xl text-gray-400 mb-4" />
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Efficiency Timeline</h4>
                  <p className="text-xs text-gray-500 max-w-sm">
                    Historical view of usage vs cost efficiency ratio over the last 12 months
                  </p>
                  <div className="mt-4 flex justify-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                      <span className="text-xs text-gray-600">Efficient</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
                      <span className="text-xs text-gray-600">Warning</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                      <span className="text-xs text-gray-600">Needs Attention</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Insights */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Key Optimization Insights</h4>
                <div className="space-y-2">
                  <div className="border-l-4 border-green-500 bg-green-50 p-3 rounded-r">
                    <div className="flex items-start space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5" />
                      <div>
                        <div className="text-xs font-medium text-green-900">Compute Optimization Success</div>
                        <div className="text-xs text-green-700 mt-1">
                          Reserved instances and spot pricing strategies reduced compute costs by 62% relative to usage growth
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-yellow-500 bg-yellow-50 p-3 rounded-r">
                    <div className="flex items-start space-x-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5" />
                      <div>
                        <div className="text-xs font-medium text-yellow-900">Storage Needs Attention</div>
                        <div className="text-xs text-yellow-700 mt-1">
                          Storage costs growing faster than usage. Consider lifecycle policies and data archiving
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-green-500 bg-green-50 p-3 rounded-r">
                    <div className="flex items-start space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5" />
                      <div>
                        <div className="text-xs font-medium text-green-900">Network Optimization Excellent</div>
                        <div className="text-xs text-green-700 mt-1">
                          CDN and data transfer optimization reduced network costs by 71% relative to usage
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Forecast Projections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* 12-Month Forecast */}
            <div className="bg-white border border-gray-300 rounded p-3">
              <h3 className="text-sm font-bold text-gray-900 mb-2">12-Month Usage & Cost Forecast</h3>
              <div className="space-y-3">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-900">Current Efficiency Score</span>
                    <span className="text-lg font-bold text-green-600">73/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '73%'}}></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Based on current optimization trends</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">+18.5%</div>
                    <div className="text-xs text-gray-600">Projected Usage Growth</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">+11.2%</div>
                    <div className="text-xs text-gray-600">Projected Cost Growth</div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <div className="text-sm font-medium text-green-900">Expected Savings</div>
                  <div className="text-lg font-bold text-green-600">$24,500/month</div>
                  <div className="text-xs text-green-700">If optimization trends continue</div>
                </div>
              </div>
            </div>

            {/* Optimization Recommendations */}
            <div className="bg-white border border-gray-300 rounded p-3">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Optimization Recommendations</h3>
              <div className="space-y-3">
                <div className="border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faDatabase} className="text-yellow-600" />
                      <span className="text-xs font-medium text-gray-900">Storage Lifecycle</span>
                    </div>
                    <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-1 rounded">High Impact</span>
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    Implement automated data archiving for files older than 90 days
                  </div>
                  <div className="text-xs text-green-600 font-medium">
                    Potential savings: $8,200/month
                  </div>
                </div>

                <div className="border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faServer} className="text-blue-600" />
                      <span className="text-xs font-medium text-gray-900">Right-sizing</span>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">Medium Impact</span>
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    Downsize overprovisioned instances with low CPU utilization
                  </div>
                  <div className="text-xs text-green-600 font-medium">
                    Potential savings: $3,400/month
                  </div>
                </div>

                <div className="border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faNetworkWired} className="text-purple-600" />
                      <span className="text-xs font-medium text-gray-900">CDN Expansion</span>
                    </div>
                    <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">Low Impact</span>
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    Expand CDN coverage to reduce data transfer costs
                  </div>
                  <div className="text-xs text-green-600 font-medium">
                    Potential savings: $1,200/month
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesTab;